import React, { useState, useEffect, useRef } from "react";
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  TablePagination,
  Dialog,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  Tooltip,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import DeleteIcon from "@material-ui/icons/Delete";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import DateRangeIcon from "@material-ui/icons/DateRange";
import EventBusyIcon from "@material-ui/icons/EventBusy";
import MenuIcon from "@material-ui/icons/Menu";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import RestorePageIcon from "@material-ui/icons/RestorePage";
import ViewCompactIcon from "@material-ui/icons/ViewCompact";
import CloseIcon from "@material-ui/icons/Close";
import Menu from "@material-ui/core/Menu";
import { Assignment } from "@material-ui/icons";

/* ------------------------- Importaciones internas ------------------------- */
import { api } from "../../../services/api";
import { handleGenericUpdate } from "../../../core/utils/handleGeneric";
import { exportarExcel } from "../../../core/utils/excelGeneric";
import { renderEstadoTexto } from "../../../core/utils/constantes";
import { ADMINISTRACION, ADMINISTRADOR } from "../../../core/utils/roles.constants";
import { useAuth } from "../../../contexts/AuthContext";
import CrearRequisicionForm from "./adicionales/createRequisiones";
import ConfirmDialog from "../../../components/Dialogs/ConfirmDialog";
import BotonGenerico from "../../../components/Button/Button";

/* --------------------------------- Estilos -------------------------------- */
const useStyles = makeStyles((theme) => ({
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  leftButtonContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  leftButton: {
    display: "flex",
    alignItems: "center",
    textTransform: "none",
    backgroundColor: "green",
    color: "white",
    "&:hover": {
      backgroundColor: "#388e3c",
    },
  },
  buttonIcon: {
    marginRight: theme.spacing(1),
  },
  rightActionsContainer: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      justifyContent: "space-between",
    },
  },
  searchField: {
    marginRight: theme.spacing(2),
    width: "200px",
    [theme.breakpoints.down("xs")]: {
      width: "50%",
      marginBottom: theme.spacing(1),
    },
  },
  iconButtonsContainer: {
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.down("xs")]: {
      justifyContent: "flex-end",
      width: "100%",
    },
  },
  errorIcon: {
    color: "green",
    fontSize: "4rem",
    marginBottom: theme.spacing(2),
  },
  dialogTitle: {
    textAlign: "center",
  },
  dialogContent: {
    textAlign: "center",
  },
  dialogButtons: {
    justifyContent: "center",
  },
  greenButton: {
    color: "green",
    borderColor: "green",
    borderRadius: theme.shape.borderRadius,
    textTransform: "none",
  },
  menuIconButton: {
    color: theme.palette.primary.main,
  },
  menuItemIcon: {
    marginRight: theme.spacing(1),
  },
  greenIcon: {
    color: "green",
  },
  dateRangeFormContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  dateRangeFields: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    "& > *": {
      margin: theme.spacing(1),
      minWidth: "150px", // Asegura un ancho mínimo para alineación
    },
  },
  dateRangeButton: {
    marginTop: theme.spacing(2),
    alignSelf: "center", // Centrar el botón
  },
  greenBackg: {
    backgroundColor: "green",
    color: "white",
    "&:hover": {
      backgroundColor: "#388e3c",
    },
  },

  // Ancho máximo para el diálogo del modal
  dialogContentWide: {
    minWidth: "500px", // Añade un ancho mínimo de 500px
    maxWidth: "700px", // Ajusta a 700px para más espacio
    padding: theme.spacing(3), // Más espacio interno para mejor legibilidad
  },
  dialogTitleClos: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  // Para evitar el solapamiento del texto
  dialogTitleText: {
    marginRight: theme.spacing(3), // Separa el texto del ícono de cerrar
  },
}));

const Requisiones = () => {
  const classes = useStyles();

  // Usuario local
  const { getUser } = useAuth();
  const user = getUser();

  // Estados principales
  const [dataEvents, setDataEvents] = useState([]); // Requisiciones
  const [filteredEvents, setFilteredEvents] = useState([]); // Requisiciones filtradas
  const [tipoRequisicion, setTipoRequisicion] = useState("");

  // Estados de búsqueda y paginación
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // ------------------ Estados de filtros ------------------
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedSector, setSelectedSector] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  // Estados de diálogo y formulario
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [selectedRequisition, setSelectedRequisition] = useState(null);

  // Estados de menú y visualización
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [showHiddenRecords, setShowHiddenRecords] = useState(false);

  const [visibleColumns, setVisibleColumns] = useState({
    Estado: true,
    NroRequisicion: true,
    Fecha: true,
    Concepto: false,
    Sector: true,
    Importe: false,
    Observaciones: true,
  });

  const [sectorFiltrado, setSectorFiltrado] = useState([]);

  // Estados de filtros por fecha/área/estado
  const [showDateRangeForm, setShowDateRangeForm] = useState(false);

  const mapasRef = useRef({}); // Almacena los mapas para formateo

  const fetchData = async () => {
    try {
      // Realizar todas las peticiones en paralelo
      const [
        requisicionesResponse,
        ordenesDeCompraResponse,
        sectorResponse,
        itemResponse,
        itemsRequisicionesResponse
      ] = await Promise.all([
        api.requisiciones.getAll(),
        api.ordenesCompra.getAll(),
        api.sectores.getAll(),
        api.items.getAll(),
        api.itemsRequisiciones.getAll(),
      ]);

      if (requisicionesResponse.length === 0) {
        setDataEvents([]);
        setFilteredEvents([]);
        return;
      }

      const createMap = (arr, keyField, valueField = null) => {
        return arr.reduce((map, item) => {
          map[item[keyField]] = valueField ? item[valueField] : item;
          return map;
        }, {});
      };

      setSectorFiltrado(sectorResponse.filter(s => s.oculto === 0));

      const requisicionesMap = createMap(requisicionesResponse, 'idRequision', 'Fecha_creacion');
      const ordenesDeCompraMap = createMap(ordenesDeCompraResponse, 'IdOC', 'Fecha_Creacion');
      const sectorMap = createMap(sectorResponse, 'IdSector', 'Nombre');
      const itemMap = createMap(itemResponse, 'IdItems');

      const itemsRequisicionesMap = itemsRequisicionesResponse.reduce((map, item) => {
        const id = item.idRequision;
        if (!map[id]) map[id] = [];
        map[id].push(item);
        return map;
      }, {});

      // Guardar los mapas en el ref para futuras ediciones
      mapasRef.current = {
        requisicionesMap,
        ordenesDeCompraMap,
        sectorMap,
        itemMap,
        itemsRequisicionesMap
      };

      // Formatear todas las requisiciones
      const requisicionesConFecha = requisicionesResponse.map(r => {
        const requisicion = formatearRequisicion(r, mapasRef.current);
        if (requisicion.Observaciones === "") {
          requisicion.Observaciones = "Sin observación";
        }
        return requisicion;
      }).filter((r) => {
        const sectores = Array.isArray(user.sectores) && user.sectores.length > 0
          ? user.sectores.map(Number) : [Number(user.IdSector)];
        return user.es_admin === ADMINISTRADOR || user.es_admin === ADMINISTRACION || sectores.includes(Number(r.IdSector));
      }).reverse();
      // }).filter((r) => r.IdSector === user.IdSector).reverse();

      setDataEvents(requisicionesConFecha);
      setFilteredEvents(requisicionesConFecha);
    } catch (error) {
      console.error("Error al cargar requisiciones:", error);
    }
  };

  const formatearRequisicion = (requisicion, maps) => {
    const {
      requisicionesMap,
      sectorMap,
      itemsRequisicionesMap
    } = maps;

    const id = requisicion.idRequision;

    // Si no hay items asociados a la requisición, usar array vacío

    const itemsReqList = itemsRequisicionesMap[id] || [];

    const itemsConDetalle = itemsReqList.map(itemReq => {
      const cantidad = parseFloat(itemReq.cantidad) || 0;
      const importeUnitario = parseFloat(itemReq.importeUnitario) || 0;
      const subtotal = importeUnitario * cantidad;

      return {
        ...itemReq,
        nombreItem: itemReq.itemName || '',
        importeUnitario,
        cantidad,
        subtotal,
      };
    }).filter(Boolean); // eliminar nulos

    const totalImporte = itemsConDetalle.reduce((acc, item) => acc + item.subtotal, 0);

    return {
      ...requisicion,
      Fecha: requisicionesMap[id],
      Sector: sectorMap[requisicion.IdSector],
      ItemsRequisicion: itemsConDetalle, // opcionalmente podés renombrarlo
      Importe: totalImporte,
    };
  };

  const handleSuccess = async (nuevaRequisicion) => {
    const existe = dataEvents.some(item => item.idRequision === nuevaRequisicion.idRequision);

    let nuevaLista;

    if (existe) {
      try {
        // FIX: Falta analizar
        // const itemsActualizados = await api.itemsRequisicionesXRequisicion(nuevaRequisicion.idRequision);

        // const requisicionActualizada = await api.requisiciones.getById(nuevaRequisicion.idRequision);

        // // Obtener solo los ids válidos y únicos
        // const nuevosIdItemOS = [...new Set(itemsActualizados.map(it => it.idItemOS).filter(Boolean))];

        // // Intentar cargar cada itemOS, pero filtrar errores 404
        // const nuevosItemOS = (await Promise.allSettled(
        //   nuevosIdItemOS.map(id => api.itemsOS.getById(id))
        // )).filter(res => res.status === 'fulfilled').map(res => res.value);

        // nuevosItemOS.forEach(itemOS => {
        //   mapasRef.current.itemOSMap[itemOS.idItemOS] = itemOS;
        // });

        // const nuevosIdItems = [...new Set(nuevosItemOS.map(os => os.idItems).filter(Boolean))];
        // const nuevosItems = (await Promise.allSettled(
        //   nuevosIdItems.map(id => api.items.getById(id))
        // )).filter(res => res.status === 'fulfilled').map(res => res.value);

        // nuevosItems.forEach(item => {
        //   mapasRef.current.itemMap[item.idItems] = item;
        // });

        // requisicionActualizada.ItemsOS = itemsActualizados;

        // const requisicionFormateadaActualizada = formatearRequisicion(requisicionActualizada, mapasRef.current);

        // nuevaLista = dataEvents.map(item =>
        //   item.idRequision === nuevaRequisicion.idRequision
        //     ? requisicionFormateadaActualizada
        //     : item
        // );

        window.location.reload();

      } catch (error) {
        console.error("Error al obtener la requisición actualizada:", error);
        return;
      }
    } else {
      const itemsRequisicionesResponse = await api.itemsRequisiciones.getAll()

      const itemsRequisicionesMap = itemsRequisicionesResponse.reduce((map, item) => {
        const id = item.idRequision;
        if (!map[id]) map[id] = [];
        map[id].push(item);
        return map;
      }, {});

      mapasRef.current.itemsRequisicionesMap = itemsRequisicionesMap;

      const requisicionFormateada = formatearRequisicion(nuevaRequisicion, mapasRef.current);

      nuevaLista = [requisicionFormateada, ...dataEvents];
    }

    setDataEvents(nuevaLista);
    setFilteredEvents(nuevaLista);
  };

  const handleUpdateEstado = (nuevoEstado) => {
    if (!selectedRequisition) return; // Si no hay fila seleccionada, no hacer nada

    const nuevaRequisicion = { ...selectedRequisition, IdEstado: nuevoEstado };

    const updatedData = dataEvents.map((item) =>
      item.idRequision === selectedRequisition.idRequision ? nuevaRequisicion : item
    );

    setDataEvents(updatedData);
    setFilteredEvents(updatedData); // Aquí también actualizas filteredEvents
    setSelectedRow(nuevaRequisicion); // Para que se actualice en la UI

    handleCloseForm()
  };

  const visibleRows = filteredEvents.filter(
    (row) => showHiddenRecords ? row.requisionOculto : !row.requisionOculto
  );

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const visibleCount = filteredEvents.filter(
      (row) => showHiddenRecords ? row.requisionOculto : !row.requisionOculto
    ).length;

    if (page > 0 && page * rowsPerPage >= visibleCount) {
      setPage(Math.max(0, Math.ceil(visibleCount / rowsPerPage) - 1));
    }
  }, [filteredEvents, page, rowsPerPage, showHiddenRecords]);

  useEffect(() => {
    let temp = [...dataEvents];

    // Filtrar por oculto
    temp = temp.filter((row) => showHiddenRecords ? row.requisionOculto : !row.requisionOculto);

    // Filtrar por sector
    if (selectedSector) {
      temp = temp.filter((row) => row.IdSector === selectedSector);
    }

    // Filtrar por estado
    if (selectedStatus) {
      temp = temp.filter((row) => row.Estado === selectedStatus || row.IdEstado === selectedStatus);
    }

    // Filtrar por rango de fechas
    if (startDate) {
      temp = temp.filter((row) => new Date(row.Fecha_creacion) >= new Date(startDate));
    }
    if (endDate) {
      temp = temp.filter((row) => new Date(row.Fecha_creacion) <= new Date(endDate));
    }

    // Filtro de búsqueda
    if (searchText) {
      const text = searchText.toLowerCase();
      temp = temp.filter((event) =>
        Object.values(event).some(
          (value) =>
            value !== null && value !== undefined && String(value).toLowerCase().includes(text)
        )
      );
    }

    setFilteredEvents(temp);
    setPage(0);
  }, [dataEvents, searchText, selectedSector, selectedStatus, startDate, endDate, showHiddenRecords]);

  const handleSearchChange = (event) => {
    const text = event.target.value.toLowerCase();
    setSearchText(text);

    if (text === "") {
      setFilteredEvents(dataEvents);
    } else {
      const filtered = dataEvents.filter((event) =>
        Object.values(event).some((value) =>
          value !== null && value !== undefined && String(value).toLowerCase().includes(text)
        )
      );
      setFilteredEvents(filtered);
    }

    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (row, index) => {
    setSelectedRow(row);
    // setSelectedIndex(index)
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setSelectedRow(null);
    // setSelectedIndex(null);
  };

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const toggleHiddenRecords = () => {
    setShowHiddenRecords((prev) => !prev);
    handleMenuClose();
    setPage(0)
  };

  const exportToCSV = async () => {
    const columnas = [
      { key: 'IdEstado', label: 'Estado' },
      { key: 'NroRequisicion', label: 'Nro Requisición' },
      { key: 'Fecha_creacion', label: 'Fecha' },
      { key: 'Concepto', label: 'Concepto' },
      { key: 'Sector', label: 'Sector' },
      { key: 'Importe', label: 'Importe' },
      { key: 'Observaciones', label: 'Observaciones' }
    ];

    const formatearDato = (key, valor) => {
      switch (key) {
        case 'IdEstado':
          return valor ? renderEstadoTexto(valor) : 'Sin Estado';
        case 'NroRequisicion':
          return valor || 'No disponible';
        case 'Fecha_creacion':
          return valor || 'Fecha no disponible';
        case 'Concepto':
          return valor || 'Sin Concepto';
        case 'Importe':
          return valor ? `$${valor}` : 'No disponible';
        case 'Observaciones':
          return valor?.trim() ? valor : 'Sin observación';
        default:
          return valor ?? '';
      }
    };

    // ✅ Crea objetos con claves de etiqueta (label), no key interna
    // const datosExcel = filteredEvents.map(row => {
    const datosExcel = visibleRows.map(row => {
      const fila = {};
      columnas.forEach(col => {
        fila[col.label] = formatearDato(col.key, row[col.key]);
      });
      return fila;
    });

    if (datosExcel.length > 0) {
      await exportarExcel('requisiciones', datosExcel);
    }
  };

  const renderEstadoIcon = (estado) => {
    const estadoColor = {
      1: "orange",  // Borrador
      2: "orange",  // Enviado a aprobar
      3: "green",   // Aprobado
      4: "red",     // Cancelado
    };

    const estadoTooltip = {
      1: "Borrador",           // Tooltip para Borrador
      2: "Pendiente de aprobación",  // Tooltip para Enviado a aprobar
      3: "Cerrado",           // Tooltip para Aprobado
      4: "Cancelado",          // Tooltip para Cancelado
    };

    // Verifica si el estado es válido y existe en ambos objetos
    if (!estadoColor[estado] || !estadoTooltip[estado]) return null;

    const color = estadoColor[estado];
    const tooltipText = estadoTooltip[estado];

    const icon = {
      1: Assignment,
      2: HourglassEmptyIcon,
      3: CheckCircleIcon,
      4: CancelIcon,
    };

    const Icon = icon[estado] || HourglassEmptyIcon;

    return (
      <Tooltip title={<span style={{ fontSize: "0.8rem" }}>{tooltipText}</span>} arrow>
        <Icon style={{ color }} />
      </Tooltip>
    );
  };

  const handleDateRangeClick = () => {
    setShowDateRangeForm(!showDateRangeForm);
  };

  const handleColumnConfigOpen = () => setColumnConfigOpen(true);
  const handleColumnConfigClose = () => setColumnConfigOpen(false);

  const handleColumnVisibilityChange = (column) => {
    setVisibleColumns((prev) => ({ ...prev, [column]: !prev[column] }));
  };

  const handleOpenForm = () => setOpenForm(true);
  const handleCloseForm = () => { setOpenForm(false); setSelectedRequisition(null); };

  return (
    <div>
      <div className={classes.headerContainer}>
        <div className={classes.leftButtonContainer}>
          <BotonGenerico
            tipo={"nuevo"}
            color="primary"
            onClick={() => { handleOpenForm(); setTipoRequisicion("Nueva Requisición"); }}
            text="Nuevo"
          />
          <CrearRequisicionForm
            open={openForm}
            onClose={handleCloseForm}
            onSuccess={handleSuccess}
            onUpdateEstado={handleUpdateEstado}
            requisicion={selectedRequisition}
            tipoRequisicion={tipoRequisicion}
            usuarioLocal={user}
          />
        </div>
        {showHiddenRecords && (
          <Typography variant="body1">
            Las requisiciones solo pueden ser editadas si fueron restauradas
          </Typography>
        )}
        <div className={classes.rightActionsContainer}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Buscar"
            value={searchText}
            onChange={handleSearchChange}
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <div className={classes.iconButtonsContainer}>
            <IconButton color="primary" onClick={handleDateRangeClick}>
              {showDateRangeForm ? <EventBusyIcon /> : <DateRangeIcon />}
            </IconButton>
            <IconButton
              className={classes.menuIconButton}
              onClick={handleMenuOpen}
            >
              <MenuIcon className={classes.greenIcon} />
            </IconButton>
            <Menu
              anchorEl={menuAnchorEl}
              keepMounted
              open={Boolean(menuAnchorEl)}
              onClose={handleMenuClose}
            >
              {/* {user && user.es_admin === 1 && ( */}
              <MenuItem onClick={toggleHiddenRecords}>
                <VisibilityOffIcon
                  className={`${classes.menuItemIcon} ${classes.greenIcon}`}
                />
                {showHiddenRecords ? "Ver registros activos" : "Ver registros ocultos"}
              </MenuItem>
              {/* )} */}
              <MenuItem onClick={exportToCSV}>
                <RestorePageIcon
                  className={`${classes.menuItemIcon} ${classes.greenIcon}`}
                />
                Exportar Registros
              </MenuItem>
              <MenuItem onClick={handleColumnConfigOpen}>
                <ViewCompactIcon
                  className={`${classes.menuItemIcon} ${classes.greenIcon}`}
                />
                Configurar Columnas
              </MenuItem>
            </Menu>
          </div>
        </div>
      </div>

      {showDateRangeForm && (
        <div className={classes.dateRangeFormContainer}>
          <div className={classes.dateRangeFields}>
            <InputLabel>Desde:</InputLabel>
            <TextField variant="outlined" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <InputLabel>Hasta:</InputLabel>
            <TextField variant="outlined" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            <FormControl variant="outlined">
              <InputLabel>Sectores</InputLabel>
              <Select value={selectedSector} onChange={(e) => setSelectedSector(Number(e.target.value))}>
                <MenuItem value="">Todos</MenuItem>
                {sectorFiltrado.map((s) => <MenuItem key={s.IdSector} value={s.IdSector}>{s.Nombre}</MenuItem>)}
              </Select>
            </FormControl>
          </div>
          <BotonGenerico color="primary" onClick={() => { setStartDate(""); setEndDate(""); setSelectedSector(""); setSelectedStatus(""); }} text="Borrar filtros" />
        </div>
      )}

      <Dialog
        open={columnConfigOpen}
        onClose={handleCloseDialog}
        classes={{ paper: classes.dialogContentWide }}
      >
        <DialogTitle className={classes.dialogTitleClos}>
          Configurar Columnas
          <IconButton
            onClick={handleColumnConfigClose}
            style={{ position: "absolute", right: 10, top: 10 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {Object.keys(visibleColumns).map((column) => (
            <div key={column} style={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                checked={visibleColumns[column]}
                onChange={() => handleColumnVisibilityChange(column)}
              />
              <Typography>{column}</Typography>
            </div>
          ))}
        </DialogContent>
      </Dialog>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              {visibleColumns.Estado && <TableCell align="center" style={{ width: "3%" }}>Estado</TableCell>}
              {visibleColumns.NroRequisicion && <TableCell align="center" style={{ width: "3%" }}>NroRequisicion</TableCell>}
              {visibleColumns.Fecha && <TableCell align="center" style={{ width: "17%" }}>Fecha</TableCell>}
              {visibleColumns.Concepto && <TableCell align="center" style={{ width: "15%" }}>Concepto</TableCell>}
              {visibleColumns.Sector && <TableCell align="center" style={{ width: "15%" }}>Sector</TableCell>}
              {visibleColumns.Importe && <TableCell align="center" style={{ width: "10%" }}>Importe</TableCell>}
              {visibleColumns.Observaciones && <TableCell align="center" style={{ width: "40%" }}>Observaciones</TableCell>}
              <TableCell align="center" style={{ width: "10%" }}>Opciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length > 0 ? (
              visibleRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => (
                  <TableRow key={`item-${index}`}>
                    {visibleColumns.Estado && <TableCell style={{ width: "3%" }}>{row.IdEstado ? renderEstadoIcon(row.IdEstado) : "Sin Estado"}</TableCell>}
                    {visibleColumns.NroRequisicion && <TableCell style={{ width: "3%" }}>{row.NroRequisicion || "No disponible"}</TableCell>}
                    {visibleColumns.Fecha && <TableCell align="center" style={{ width: "17%" }}>{row.Fecha_creacion || "Fecha no disponible"}</TableCell>}
                    {visibleColumns.Concepto && <TableCell align="center" style={{ width: "15%" }}>{row.Concepto || "Sin Concepto"}</TableCell>}
                    {visibleColumns.Sector && <TableCell align="center" style={{ width: "15%" }}>{row.IdSector ? `${row.Sector}` : "Sin Sector"}</TableCell>}
                    {visibleColumns.Importe && <TableCell align="center" style={{ width: "10%" }}>{row.Importe ? `$${row.Importe}` : "No disponible"}</TableCell>}
                    {visibleColumns.Observaciones && <TableCell align="center" style={{ width: "40%" }}>{row.Observaciones.trim() ? row.Observaciones : "Sin observación"}</TableCell>}
                    <TableCell align="center" style={{ width: "10%" }}>
                      {!showHiddenRecords && (
                        <IconButton onClick={() => { setSelectedRequisition(row); setOpenForm(true); setTipoRequisicion(`Editando Requisición ${row.NroRequisicion}`); }}>
                          <SearchIcon color="primary" />
                        </IconButton>
                      )}
                      {/* {!showHiddenRecords && user && user.es_admin !== 4 && ( */}
                      {!showHiddenRecords && user && (
                        <IconButton onClick={() => handleOpenDialog(row, index)} className={classes.greenButton}>
                          <DeleteIcon color="secondary" />
                        </IconButton>
                      )}
                      {/* {showHiddenRecords && user && user.es_admin === 1 && ( */}
                      {showHiddenRecords && user && (
                        <IconButton onClick={() => handleOpenDialog(row, index)} className={classes.greenButton}>
                          <RestorePageIcon color="secondary" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
            ) : (
              <TableRow>
                <TableCell colSpan={Object.values(visibleColumns).filter(Boolean).length} align="center">
                  No hay requisiciones para mostrar
                </TableCell>
              </TableRow>
            )}
          </TableBody>

        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={visibleRows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página"
      />

      <ConfirmDialog
        open={open}
        onClose={handleCloseDialog}
        onConfirm={() =>
          handleGenericUpdate({
            selectedItem: selectedRow,
            data: dataEvents,
            setData: (newData) => {
              setDataEvents(newData);
              setFilteredEvents(
                newData.filter(
                  (row) => showHiddenRecords ? row.requisionOculto : !row.requisionOculto
                )
              );
            },
            apiUpdateFunction: api.requisiciones.update,
            idField: "idRequision",
            updatePayload: showHiddenRecords
              ? { requisionOculto: false }
              : { requisionOculto: true },
            closeDialog: handleCloseDialog,
          })
        }
        title={!showHiddenRecords ? "Ocultar Registro" : "Recuperar Registro"}
        description={!showHiddenRecords ? "¿Está seguro de que quiere ocultar el registro?" : "¿Está seguro de que quiere recuperar el registro?"}
        confirmText={!showHiddenRecords ? "Ocultar registro" : "Recuperar registro"}
        cancelText="Cancelar"
        icon={<ErrorOutlineIcon className={classes.errorIcon} />}
        classes={{
          dialogTitle: classes.dialogTitle,
          dialogContent: classes.dialogContent,
          dialogButtons: classes.dialogButtons,
          confirmButton: classes.greenButton,
          cancelButton: classes.greenButton,
          iconWrapper: classes.iconWrapper,
        }}
      />
    </div>
  );
};

export default Requisiones;
